import React, { lazy, ReactElement, useEffect, useState } from 'react';
import { gsap } from 'gsap';
import { useRouter } from 'next/router';
import find from 'lodash/find';
import { toggleModal, toggleShowMobileMenu } from '@Hotels-at-Home/hah-fe-core/dist/store/modal';
import { selectShowModal } from '@Hotels-at-Home/hah-fe-core/dist/storeSelectors/modal';
import {
  useAnalyticsScripts,
  useGetPageClass,
  useConsentModeListener,
  useInterval,
  useFirstVisit,
  useWindowSize,
  useScrollRestoration,
  useCatalogId,
  useRumScript,
} from '@Hotels-at-Home/hah-fe-core/dist/hooks';
import {
  SM_WIDTH,
  COOKIE_CHECKOUT_START,
  COOKIE_CURRENCY_NAME,
  COOKIE_LANGUAGE_NAME,
  LanguageCode,
} from '@Hotels-at-Home/hah-fe-core/dist/constants';
import Header from '@Hotels-at-Home/hah-fe-core/dist/components/Header';
import SearchDrawer from '@Hotels-at-Home/hah-fe-core/dist/components/SearchDrawer';
import ShoppingCartDrawer from '@Hotels-at-Home/hah-fe-core/dist/components/CartDrawer';
import BannerWrapper from '@Hotels-at-Home/hah-fe-core/dist/components/BannerWrapper';
import {
  ActionType,
  IFooter,
  INavMenu,
  ModalType,
  IGetPromotionResponse,
  UtilityIcons,
} from '@Hotels-at-Home/hah-fe-core/dist/types';

import { useDispatch, useSelector } from '~/store/hooks';
import {
  getCookie,
  getCookies,
  IConsentMode,
  removeCookie,
  setFirstVisitCookie,
  setPreferredLangCookie,
} from '@Hotels-at-Home/hah-fe-core/dist/utils/cookies';
import Footer from '../components/layouts/Footer';
import { CurrencyDrawer, LanguageDrawer } from '@Hotels-at-Home/hah-fe-core/dist/components';

import {
  cloudWatchRum,
  cookieRestorerEnabled,
  cookieRestorerOptions,
} from '~/constants/siteConfig';

import classnames from 'classnames';

import FontFaceObserver from 'fontfaceobserver';
import { getHrefLinks } from '~/utils/hreflangs';
import { getTermsAndConditionsByCurrency } from '@Hotels-at-Home/hah-fe-core/dist/utils/promotion';

import Modal from '@Hotels-at-Home/hah-fe-core/dist/components/Modal';
import BmsmChart from '../components/common/Banner/BmsmChart';
import { CookieRestorer } from '@Hotels-at-Home/hah-fe-core/dist/utils/cookieRestorer';

const FirstVisitModal = lazy(
  () => import('@Hotels-at-Home/hah-fe-core/dist/components/FirstVisitModal'),
);

interface IProps {
  userAgent: string;
  children: React.ReactNode;
  navMenu: INavMenu;
  footer: IFooter;
  promotion: IGetPromotionResponse;
  networkErrorStatusCode: number;
  isSafari: boolean;
}

const AppLayout = ({
  userAgent,
  children,
  navMenu,
  footer,
  promotion,
  networkErrorStatusCode,
  isSafari,
}: IProps): ReactElement => {
  useCatalogId();
  useScrollRestoration();
  const { pathname, route } = useRouter();
  const [bodyFontLoaded, setBodyFontLoaded] = useState(false);
  const [headingFontLoaded, setHeadingFontLoaded] = useState(false);
  const [consentMode, setConsentMode] = useState<IConsentMode | null>(null);
  const [shouldIntervalBeCancelled, setShouldIntervalBeCancelled] = useState(false);

  const windowSize = useWindowSize();
  const isSmallScreen = Boolean(windowSize.width) && windowSize.width <= SM_WIDTH;

  useConsentModeListener((consent: any) => {
    setConsentMode(consent);
  });

  const currency = getCookie(COOKIE_CURRENCY_NAME) as string;
  const lang = getCookie(COOKIE_LANGUAGE_NAME) as LanguageCode;

  useAnalyticsScripts();

  // CloudWatch RUM: real-user monitoring, gated on Cookiebot statistics consent
  // inside the hook. See cloudwatch-rum-gdpr-investigation.md in hah-docs.
  useRumScript(cloudWatchRum);

  const dispatch = useDispatch();
  const showModal = useSelector(selectShowModal);

  gsap.config({ nullTargetWarn: false });
  const cookies = getCookies();

  // Override GSAP-set inline styles from header elements
  // GSAP in Header component sets paddingTop/paddingBottom and logo height via inline styles
  // TODO: We should be able to pass a prop to the Header component to disable these animations
  /* useEffect(() => {
    const header = document.querySelector('header');
    const logoLink = document.querySelector('.main-logo a') as HTMLElement;
    if (!header || !logoLink) return;

    let ticking = false;
    let isScrolled = false;
    const SCROLL_THRESHOLD = 50; // Larger threshold to prevent jitter

    // Function to override GSAP-set inline styles
    const overrideGSAPStyles = () => {
      const { scrollY } = window;

      // Clear header padding always
      header.style.paddingTop = '0';
      header.style.paddingBottom = '0';

      // Use hysteresis: different thresholds for scrolling down vs up
      // This prevents jitter at the boundary
      const shouldBeSmall = isScrolled ? scrollY > 10 : scrollY > SCROLL_THRESHOLD;

      if (shouldBeSmall && !isScrolled) {
        // Transitioning to scrolled state
        logoLink.style.setProperty('height', '95px', 'important');
        logoLink.style.setProperty('width', '160px', 'important');
        logoLink.style.setProperty('margin-bottom', '0', 'important');
        isScrolled = true;
      } else if (!shouldBeSmall && isScrolled) {
        // Transitioning back to top
        logoLink.style.removeProperty('height');
        logoLink.style.removeProperty('width');
        logoLink.style.removeProperty('margin-bottom');
        isScrolled = false;
      }

      ticking = false;
    };

    // Optimized scroll handler
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(overrideGSAPStyles);
        ticking = true;
      }
    };

    // Watch for GSAP mutations and immediately override
    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          const target = mutation.target as HTMLElement;

          // If this is the header, clear padding
          if (target === header) {
            if (header.style.paddingTop !== '0') {
              header.style.paddingTop = '0';
            }
            if (header.style.paddingBottom !== '0') {
              header.style.paddingBottom = '0';
            }
          }

          // If this is the logo link and we're in scrolled state, enforce our sizes
          if (target === logoLink && isScrolled) {
            const currentHeight = logoLink.style.height;
            const currentWidth = logoLink.style.width;

            // Only update if GSAP changed it to something other than our values
            if (currentHeight && currentHeight !== '95px') {
              logoLink.style.setProperty('height', '95px', 'important');
            }
            if (!currentWidth || currentWidth !== '160px') {
              logoLink.style.setProperty('width', '160px', 'important');
            }
            if (logoLink.style.marginBottom && logoLink.style.marginBottom !== '0') {
              logoLink.style.setProperty('margin-bottom', '0', 'important');
            }
          }
        }
      });
    });

    // Observe both header and logo link
    observer.observe(header, { attributes: true, attributeFilter: ['style'] });
    observer.observe(logoLink, { attributes: true, attributeFilter: ['style'] });

    // Listen to scroll
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Set initial state based on current scroll position
    if (window.scrollY > SCROLL_THRESHOLD) {
      isScrolled = true;
    }
    overrideGSAPStyles();

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, []); */

  const pageClass = useGetPageClass();

  const termsAndConditionsByCurrency: string = getTermsAndConditionsByCurrency(
    promotion?.promotion_terms,
    currency,
  );

  const handleToggleModal = (modal: ModalType) => {
    dispatch(toggleModal(modal));
  };

  const onAction = (action: ActionType) => {
    let modalType;

    switch (action) {
      case ActionType.Account: {
        modalType = ModalType.Account;
        break;
      }
      case ActionType.Search: {
        modalType = ModalType.Search;
        break;
      }
      case ActionType.Language: {
        modalType = ModalType.Language;
        break;
      }
      case ActionType.ShoppingCart: {
        modalType = ModalType.ShoppingCart;
        break;
      }
      case ActionType.Currency: {
        modalType = ModalType.Currency;
        break;
      }
      default: {
        break;
      }
    }

    handleToggleModal(modalType);
  };

  const closeHandler = () => {
    if (showModal) {
      const idString = `${showModal.toLocaleLowerCase()}-button`;
      const actionButton = document.getElementById(idString);
      if (actionButton) {
        actionButton.focus();
      }
    }
    dispatch(toggleModal(null));
    dispatch(toggleShowMobileMenu(null));
  };

  const removeCheckoutCookies = () => {
    if (!pathname.includes('checkout') && cookies[COOKIE_CHECKOUT_START]) {
      removeCookie(COOKIE_CHECKOUT_START);
    }
  };

  const { isFirstVisitModalOpened, setIsFirstVisitModalOpened, handleClose } = useFirstVisit();

  useEffect(() => {
    if (!cookieRestorerEnabled || !isSafari) return;

    CookieRestorer(cookieRestorerOptions);

    const interval = setInterval(() => {
      CookieRestorer(cookieRestorerOptions);
    }, cookieRestorerOptions.options.interval || 5000);

    // eslint-disable-next-line consistent-return
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    removeCheckoutCookies();
  }, [pathname]);

  const isHomepage = !route.includes('/p/') && !route.includes('/c/');

  const isMiscPages =
    route.includes('/404') ||
    route.includes('/search') ||
    route.includes('/cart') ||
    route.includes('/help');

  const hrefLinks = getHrefLinks();

  const checkWindowConsentMode = () => {
    const consentCookie = getCookie('CookieConsent') as IConsentMode;

    if (consentCookie) {
      const consent: IConsentMode = consentCookie;

      if (consent) {
        setConsentMode(consent);

        setShouldIntervalBeCancelled(true);
      }
    }

    if (
      window?.CookieConsent?.consent !== undefined &&
      Object.values(window.CookieConsent.consent).every(e => e)
    ) {
      setFirstVisitCookie(userAgent, window.CookieConsent.consent);

      setConsentMode(window.CookieConsent.consent);

      setShouldIntervalBeCancelled(true);
    }
    if (
      window?.ConsentMode?.consent !== undefined &&
      Object.values(window.ConsentMode.consent).every(e => e)
    ) {
      setConsentMode(window.ConsentMode.consent);
      setShouldIntervalBeCancelled(true);
    }
    if (
      window?.Cookiebot?.consent !== undefined &&
      Object.values(window.Cookiebot.consent).every(e => e)
    ) {
      setConsentMode(window.Cookiebot.consent);
      setShouldIntervalBeCancelled(true);
    }
  };

  useInterval(
    () => {
      checkWindowConsentMode();
    },
    shouldIntervalBeCancelled ? null : 1000,
  );

  useEffect(() => {
    if (consentMode) {
      setPreferredLangCookie(userAgent, currency, lang);
    }
  }, [consentMode]);

  const [showBMSMModal, setShowBMSMModal] = useState(false);

  const endDate = new Date(promotion?.promo_end || new Date());
  const endDay = endDate.toLocaleDateString('en-US', { weekday: 'long' });

  const openPopup = () => {
    setShowBMSMModal(true);
  };

  const closePopup = () => {
    setShowBMSMModal(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      openPopup();
    }
  };

  let RenderBanners;

  const countdownIsActive = promotion?.promo_days_left <= 3;

  if (promotion?.promo_type !== 'bmsm') {
    RenderBanners = <BannerWrapper promotion={promotion} />;
  } else if (
    (promotion?.promo_is_active && promotion?.promo_type === 'bmsm' && !isHomepage) ||
    (promotion?.promo_is_active && promotion?.promo_type === 'bmsm' && isMiscPages)
  ) {
    RenderBanners = (
      <>
        <div
          role="button"
          tabIndex={0}
          aria-pressed="false"
          className="bmsm-banner-wrapper"
          onClick={openPopup}
          onKeyDown={handleKeyDown}
        >
          <BannerWrapper promotion={promotion} />
        </div>
        <Modal
          open={showBMSMModal}
          maxWidth="sm"
          className="base-banner-lightbox bmsm-lightbox"
          onClose={closePopup}
        >
          <div className="content">
            <BmsmChart
              promotion={promotion}
              daysLeft={countdownIsActive ? promotion?.promo_days_left : null}
              ends={endDay}
              currency={currency}
            />
          </div>
        </Modal>
      </>
    );
  } else {
    RenderBanners = null;
  }

  return (
    <div
      id="app"
      className={classnames(
        {
          safari: isSafari,
          'body-font-loaded': true,
          'heading-font-loaded': true,
        },
        pageClass,
      )}
    >
      <Header
        navMenu={navMenu}
        showActions={!networkErrorStatusCode}
        onAction={onAction}
        transparent
        breakNav="auto"
        sticky
        disableStyckyAnimation
        iconOrder={[
          UtilityIcons.ShoppingCart,
          UtilityIcons.Language,
          UtilityIcons.Search,
          UtilityIcons.Currency,
        ]}
      />
      {RenderBanners}
      <main id="content" className="content">
        {children}
      </main>
      <Footer
        footer={footer}
        termsAndConditions={termsAndConditionsByCurrency}
        promotion={promotion}
      />
      <SearchDrawer isOpen={showModal === ModalType.Search} onClose={closeHandler} />
      <CurrencyDrawer isOpen={showModal === ModalType.Currency} onClose={closeHandler} />
      <LanguageDrawer
        enableNA
        enableAPAC
        hrefLinks={hrefLinks}
        isOpen={showModal === ModalType.Language}
        onClose={closeHandler}
      />
      <ShoppingCartDrawer isOpen={showModal === ModalType.ShoppingCart} onClose={closeHandler} />
      {isFirstVisitModalOpened && !isSmallScreen && (
        <FirstVisitModal
          onClose={handleClose}
          privacyPolicy={find(footer?.bottomFooterLinks, { name: 'privacy-policy' })}
        />
      )}
    </div>
  );
};

export default AppLayout;
